import { EnhancedDialog } from 'components/enhanced/dialog';
import { MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { maintenanceService } from 'shared/services';
import CloseMaintenanceForm from '../form/CloseMaintenanceForm';

const TRANSLATION_BASE_PREFIX = 'entities.equipment';

/**
 * Props for the CloseMaintenanceDialog component.
 */
interface CloseMaintenanceDialogProps {
  /** Controls whether the dialog is rendered as open. */
  isOpen?: boolean;
  /** Identifier of the maintenance record to be closed. */
  maintenanceId?: number;
  /** Callback invoked when the dialog must be dismissed. */
  onClose?: () => void;
  /** Ref pointing to the parent DataGrid refresh function. */
  datagridRefresh?: MutableRefObject<(() => void) | null | any>;
}

/**
 * Dialog wrapper that renders a closure form for a maintenance record.
 * Upon submission it calls the maintenance service, refreshes the parent grid,
 * and dismisses the dialog.
 */
export default function CloseMaintenanceDialog({
  datagridRefresh,
  maintenanceId,
  onClose,
  isOpen
}: CloseMaintenanceDialogProps) {
  const { t } = useTranslation();

  /**
   * Handles form submission by persisting the closure payload,
   * refreshing the parent data grid, and dismissing the dialog.
   * @param values - Raw form values provided by CloseMaintenanceForm.
   */
  const handleSubmitClose = (values) => {
    maintenanceService.closeMaintenance(maintenanceId, values).then(() => {
      datagridRefresh.current();
    });
    onClose();
  };

  return (
    <EnhancedDialog
      open={isOpen}
      onClose={onClose}
      title={t(`${TRANSLATION_BASE_PREFIX}.close`)}
      subTitle={t(
        `${TRANSLATION_BASE_PREFIX}.close_subtitle`,
        'Completa e archivia l’intervento di manutenzione'
      )}
      submitLabel={t('global.add')}
      cancelLabel={t('actions.cancel')}
      maxWidth="md"
      submitButtonProps={{
        type: 'submit',
        form: 'closeMaintenance'
      }}>
      <CloseMaintenanceForm onSubmit={handleSubmitClose} />
    </EnhancedDialog>
  );
}
